import React, { useEffect, useRef } from 'react';
import G6, { Graph, NodeConfig, TreeGraph } from '@antv/g6';
import ResizeObserver from 'rc-resize-observer';
import { highlightRecursive, registerG6 } from './registerG6';
import { GraphProps, OverViewGraphProps } from '../../utils';
import { debounce, isNil } from 'lodash-es';
import { Modal, Themes } from '@maxtropy/components';
import { useLocation } from 'react-router-dom';
interface Props {
  theme: Themes;
  data?: OverViewGraphProps;
  fullScreen?: boolean;
  focusId?: number;
  setCurrentCircuitInfo: (v: any) => void;
  setCircuitDetailVisible: (v: boolean) => void;
}

const ViewGraph: React.FC<Props> = ({
  data,
  theme,
  fullScreen,
  focusId,
  setCurrentCircuitInfo,
  setCircuitDetailVisible,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<Graph | TreeGraph>();
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const url_circuitId = urlSearchParams.get('circuitId') || undefined;

  useEffect(() => {
    registerG6(true, undefined, theme);
  }, [theme]);

  // 全局模式
  useEffect(() => {
    if (!containerRef.current) return;

    const graph = new G6.Graph({
      container: containerRef.current,
      linkCenter: true,
      width: containerRef.current.scrollWidth,
      height: containerRef.current.scrollHeight,
      modes: {
        default: ['drag-canvas', 'zoom-canvas'],
      },
      fitCenter: false,
      defaultNode: {
        type: 'customNode',
      },
      defaultEdge: {
        type: 'customLine',
        style: {
          endArrow: true,
        },
      },
      groupByTypes: false,
      defaultCombo: {
        type: 'customComb',
      },
      layout: {
        type: 'comboCombined',
        groupByTypes: false,
        outerLayout: new G6.Layout['dagre']({
          rankdir: 'LR',
          align: 'UL',
          nodesep: 50,
          ranksep: 100,
          controlPoints: true,
        }),
        innerLayout: new G6.Layout['dagre']({
          rankdir: 'LR',
          align: 'UL',
          preventOverlap: true,
          ranksep: 100,
          nodesep: 20,
          controlPoints: true,
        }),
      },
    });
    graphRef.current = graph;

    graph.on('afterlayout', () => {
      if (url_circuitId) {
        const targetNode = graph.getNodes().find(node => {
          return node.getModel().id === url_circuitId;
        });

        if (targetNode) {
          graph.getNodes().forEach(node => node.setState('isSelected', false)); // 取消其他选中
          targetNode.setState('isSelected', true);
          graph.focusItem(targetNode.getID(), true);
        }
      } else {
        graph.moveTo(40, 30);
      }
    });

    graph.data(data);
    graph.render();

    const focusNode = graph.getNodes().find(item => {
      return item.getModel().id === String(focusId);
    });
    focusNode?.setState('isSelected', true);

    graph.on('node:click', e => {
      const shapeName = e.target.get('name');
      const nodeModel = e.item!.getModel();
      // 点击节点事件
      if (shapeName.startsWith('main-box')) {
        graph.getNodes().forEach(node => node.setState('isSelected', false));
        nodeModel.isSelected = true;
        const data = nodeModel as GraphProps & NodeConfig;

        const nodeId = e.item?.getID();
        if (!isNil(nodeId)) {
          highlightRecursive(graph, nodeId!);
        }

        if (!data.value.hasOu) {
          setCurrentCircuitInfo(undefined);
          Modal.warning({
            title: `当前无【${data.value.name}】的数据权限，请联系管理员添加。`,
          });
        } else {
          setCurrentCircuitInfo(data);
          setCircuitDetailVisible(true);
        }
      }
    });

    // hover事件
    graph.on('node:mouseenter', e => {
      const shapeName = e.target.get('name');
      if (shapeName.startsWith('main-box')) {
        graph.setItemState(e.item!, 'hover', true);
      }
    });
    graph.on('node:mouseleave', e => {
      graph.setItemState(e.item!, 'hover', false);
    });

    return () => {
      graph.destroy();
    };
  }, [data, theme]);

  const onResize = debounce((size: { width: number; height: number }) => {
    if (!containerRef.current) return;
    if (!graphRef.current) return;
    graphRef.current?.changeSize(size.width, size.height);
  }, 300);

  return (
    <ResizeObserver onResize={onResize}>
      <div
        style={{
          height: fullScreen ? '100vh' : 'calc(100vh - 216px)',
          width: '100%',
          border: '1px solid var(--mx-base-border-color)',
          backgroundImage: 'var(--mx-bg-image)',
        }}
        ref={containerRef}
      ></div>
    </ResizeObserver>
  );
};

export default ViewGraph;
